'use client';
import { Play, EyeOff, Link, Download, Zap, Loader2, Timer, HardDrive } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

const SUPABASE_PROJECT_ID = "rzavoyqahstbgzvrpqis";
const SB_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/public/highlights`;

type RenderStage = 'rendering' | 'done' | 'error';

interface RenderState {
  stage: RenderStage;
  progress: number;
  videoUrl?: string;
  fileSizeBytes?: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

export default function ClipsTab({ recaplets, globalMax, getLogo }: { recaplets: any[], globalMax: number, getLogo: any }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [renderStates, setRenderStates] = useState<Record<string, RenderState>>({});
  const [elapsed, setElapsed] = useState<Record<string, number>>({});
  // Per-recaplet set of selected clip indices. A clip is available only when
  // payload.clips[i].url is set (the ZenRows download patched it in); unavailable
  // clips are force-excluded regardless of user input because the Remotion Lambda
  // will fail fatally on a missing video src.
  const [selectedByRecaplet, setSelectedByRecaplet] = useState<Record<string, Set<number>>>({});
  const timerRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [publishedById, setPublishedById] = useState<Record<string, boolean>>({});
  const [publishedBusy, setPublishedBusy] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const seed: Record<string, boolean> = {};
    for (const r of recaplets) seed[r.id] = !!r.published_at;
    setPublishedById(seed);
  }, [recaplets]);

  const togglePublished = async (id: string, e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (publishedBusy[id]) return;
    const next = !publishedById[id];
    setPublishedBusy(prev => ({ ...prev, [id]: true }));
    setPublishedById(prev => ({ ...prev, [id]: next }));
    try {
      const res = await fetch('/api/recaplets/published', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, published: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setPublishedById(prev => ({ ...prev, [id]: !next }));
    } finally {
      setPublishedBusy(prev => ({ ...prev, [id]: false }));
    }
  };

  async function handleDownload(e: React.MouseEvent, url: string, filename: string, key: string) {
    e.preventDefault();
    e.stopPropagation();
    if (downloading[key]) return;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  }

  const clipIsAvailable = (clip: any): boolean => Boolean(clip?.url);

  const getSelectedSet = (recapletId: string, clips: any[]): Set<number> => {
    const existing = selectedByRecaplet[recapletId];
    if (existing) return existing;
    // Default: all available clips up to globalMax are selected
    const defaults = new Set<number>();
    clips.forEach((c, i) => { if (i < globalMax && clipIsAvailable(c)) defaults.add(i); });
    return defaults;
  };

  const toggleClip = (recapletId: string, idx: number, clips: any[]) => {
    setSelectedByRecaplet(prev => {
      const current = prev[recapletId] ?? getSelectedSet(recapletId, clips);
      const next = new Set(current);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...prev, [recapletId]: next };
    });
  };

  const startTimer = (id: string) => {
    if (timerRefs.current[id]) clearInterval(timerRefs.current[id]);
    const start = Date.now();
    setElapsed(prev => ({ ...prev, [id]: 0 }));
    timerRefs.current[id] = setInterval(() => {
      setElapsed(prev => ({ ...prev, [id]: Math.floor((Date.now() - start) / 1000) }));
    }, 1000);
  };

  const stopTimer = (id: string) => {
    if (timerRefs.current[id]) {
      clearInterval(timerRefs.current[id]);
      delete timerRefs.current[id];
    }
  };

  useEffect(() => {
    return () => {
      Object.values(timerRefs.current).forEach(clearInterval);
    };
  }, []);

  const formatElapsed = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const generateVideo = async (id: string, clips: any[]) => {
    try {
      const selected = getSelectedSet(id, clips);
      const selectedIndices = Array.from(selected).sort((a, b) => a - b);
      startTimer(id);
      setRenderStates(prev => ({ ...prev, [id]: { stage: 'rendering', progress: 0, startedAt: Date.now() } }));

      const renderRes = await fetch('/api/render-recap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recapletId: id, selectedIndices }),
      });

      if (!renderRes.ok) {
        const err = await renderRes.json();
        throw new Error(err.error || 'Render start failed');
      }

      const { renderId, bucketName } = await renderRes.json();

      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const progressRes = await fetch(
            `/api/render-recap?renderId=${renderId}&bucketName=${bucketName}&recapletId=${id}`
          );
          const progress = await progressRes.json();

          if (progress.fatalErrorEncountered) {
            clearInterval(poll);
            stopTimer(id);
            setRenderStates(prev => ({
              ...prev,
              [id]: { ...prev[id], stage: 'error', progress: 0, error: 'Render failed', finishedAt: Date.now() },
            }));
            return;
          }

          if (progress.done && progress.outputFile) {
            clearInterval(poll);
            stopTimer(id);
            setRenderStates(prev => ({
              ...prev,
              [id]: { ...prev[id], stage: 'done', progress: 100, videoUrl: progress.outputFile, fileSizeBytes: progress.outputSizeInBytes, finishedAt: Date.now() },
            }));
            return;
          }

          setRenderStates(prev => ({
            ...prev,
            [id]: {
              ...prev[id],
              stage: 'rendering',
              progress: Math.round((progress.overallProgress || 0) * 100),
              fileSizeBytes: progress.outputSizeInBytes,
            },
          }));
        } catch {
          clearInterval(poll);
          stopTimer(id);
          setRenderStates(prev => ({
            ...prev,
            [id]: { ...prev[id], stage: 'error', progress: 0, error: 'Progress check failed', finishedAt: Date.now() },
          }));
        }
      }, 2000);
    } catch (e: unknown) {
      stopTimer(id);
      setRenderStates(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          stage: 'error',
          progress: 0,
          error: e instanceof Error ? e.message : 'Unknown error',
          finishedAt: Date.now(),
        },
      }));
    }
  };

  const getVideoUrl = (r: any, id: string): string | undefined => {
    return renderStates[id]?.videoUrl || r.payload?.video_url;
  };

  const getVideoSrc = (game: any, clip: any) => {
    const folderName = `${game.away_team}-${game.home_team}_${game.game_id}`;

    // If on your Mac Studio, use local path
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return `/highlights/${game.game_date}/${folderName}/clip_${clip.eventId}.mp4`;
    }
    // If on the Vercel URL, use Supabase Storage
    return `${SB_URL}/${game.game_date}/${folderName}/clip_${clip.eventId}.mp4`;
  };

  return (
    <>
      <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[10px] font-black uppercase tracking-tighter text-slate-500 border-b border-white/5">
        <div className="col-span-5">Matchup</div>
        <div className="col-span-2">Date</div>
        <div className="col-span-3">Star Player</div>
        <div className="col-span-2 text-right">Action</div>
      </div>

      <div className="mt-2 space-y-1">
        {recaplets.map((r) => {
          const rs = renderStates[r.id];
          const videoUrl = getVideoUrl(r, r.id);
          const isRendering = rs && rs.stage !== 'done' && rs.stage !== 'error';

          return (
          <div key={r.id} className="group border border-white/5 rounded-md bg-white/[0.02] hover:bg-white/[0.04] transition-all">
            <div
              className="flex flex-wrap md:grid md:grid-cols-12 px-4 md:px-6 py-3 md:py-4 items-center cursor-pointer gap-2 md:gap-0"
              onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
            >
              <div className="flex items-center gap-3 md:gap-4 md:col-span-5 min-w-0">
                <div className="flex items-center gap-1.5">
                  <img src={getLogo(r.away_team)} alt="" className="w-5 h-5 object-contain" />
                  <span className="font-bold text-white text-sm">{r.away_team}</span>
                  <span className="text-slate-500 font-mono text-xs">{r.away_score ?? '--'}</span>
                </div>
                <span className="text-slate-700 mx-1">@</span>
                <div className="flex items-center gap-1.5">
                  <img src={getLogo(r.home_team)} alt="" className="w-5 h-5 object-contain" />
                  <span className="font-bold text-white text-sm">{r.home_team}</span>
                  <span className="text-slate-500 font-mono text-xs">{r.home_score ?? '--'}</span>
                </div>
              </div>
              <div className="ml-auto flex gap-2 items-center md:hidden">
                {videoUrl && (
                  <a
                    href={videoUrl}
                    title="Download video"
                    className={`p-1.5 rounded transition-colors ${downloading[`clip-${r.id}`] ? 'text-slate-500 pointer-events-none' : 'text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10'}`}
                    onClick={(e) => handleDownload(e, videoUrl, `${r.game_date}_${r.away_team}@${r.home_team}.mp4`, `clip-${r.id}`)}
                  >
                    {downloading[`clip-${r.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </a>
                )}
                <label
                  className="flex items-center gap-1 cursor-pointer p-1"
                  onClick={(e) => e.stopPropagation()}
                  title="Mark as published"
                >
                  <input
                    type="checkbox"
                    checked={!!publishedById[r.id]}
                    disabled={publishedBusy[r.id]}
                    onChange={(e) => togglePublished(r.id, e)}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-emerald-500 w-3.5 h-3.5"
                  />
                </label>
                <button title="Hide" className="p-1 hover:text-red-500 transition-colors"><EyeOff size={14} /></button>
              </div>
              <div className="w-full flex items-center gap-2 text-xs md:hidden">
                <span className="font-mono opacity-50">{r.game_date}</span>
                <span className="text-slate-600">·</span>
                <span className="text-red-400 font-bold truncate">{r.payload.starPlayer.name}</span>
                <span className="text-slate-500 font-bold truncate"> — {r.payload.starPlayer.statLine}</span>
              </div>
              <div className="hidden md:block md:col-span-2 text-xs font-mono opacity-50">{r.game_date}</div>
              <div className="hidden md:block md:col-span-3">
                <span className="text-red-400 font-bold text-xs">{r.payload.starPlayer.name}</span>
                <span className="text-slate-500 font-bold text-xs ml-1"> — {r.payload.starPlayer.statLine}</span>
              </div>
              <div className="hidden md:flex md:col-span-2 justify-end gap-3 items-center">
                {videoUrl && (
                  <a
                    href={videoUrl}
                    title="Download video"
                    className={`p-1.5 rounded transition-colors ${downloading[`clip-${r.id}`] ? 'text-slate-500 pointer-events-none' : 'text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10'}`}
                    onClick={(e) => handleDownload(e, videoUrl, `${r.game_date}_${r.away_team}@${r.home_team}.mp4`, `clip-${r.id}`)}
                  >
                    {downloading[`clip-${r.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </a>
                )}
                <button title="Hide" className="p-1 hover:text-red-500 transition-colors"><EyeOff size={14} /></button>
                <label
                  className="flex items-center gap-1.5 cursor-pointer text-[10px] font-black uppercase tracking-tighter"
                  onClick={(e) => e.stopPropagation()}
                  title="Mark as published (excludes from auto-publish)"
                >
                  <input
                    type="checkbox"
                    checked={!!publishedById[r.id]}
                    disabled={publishedBusy[r.id]}
                    onChange={(e) => togglePublished(r.id, e)}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-emerald-500 w-3.5 h-3.5"
                  />
                  <span className={publishedById[r.id] ? 'text-emerald-500' : 'text-slate-500'}>
                    Published
                  </span>
                </label>
              </div>
            </div>

            {expandedId === r.id && (
              <div className="px-6 pb-6 pt-2 border-t border-white/5 animate-in slide-in-from-top-2 duration-200 relative">
                <button
                  title="Copy JSON URL"
                  className="absolute top-2 right-4 p-1 text-slate-700 hover:text-slate-400 rounded transition-colors opacity-40 hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = `${window.location.origin}/api/ingest?table=recaplets&id=${r.id}`;
                    const ta = document.createElement('textarea');
                    ta.value = url;
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                  }}
                >
                  <Link size={12} />
                </button>
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {r.payload.clips.map((clip: any, i: number) => {
                    const available = clipIsAvailable(clip);
                    const selectedSet = getSelectedSet(r.id, r.payload.clips);
                    const isChecked = selectedSet.has(i);
                    const isIncluded = available && isChecked;
                    return (
                      <div key={i} className={`p-3 rounded-lg border transition-all ${isIncluded ? 'bg-white/5 border-white/10' : 'bg-black/40 border-white/5 opacity-30'}`}>
                        <div className="aspect-video bg-black rounded mb-3 border border-white/10 overflow-hidden relative group">
                          <video
                            src={getVideoSrc(r, clip)}
                            className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                            controls={false}
                            muted
                            onMouseEnter={(e) => e.currentTarget.play()}
                            onMouseLeave={(e) => {
                                e.currentTarget.pause();
                                e.currentTarget.currentTime = 0;
                            }}
                          />
                          <div className="absolute bottom-1 right-2 text-[8px] font-mono text-white/40 group-hover:text-white">EVT {clip.eventId}</div>
                        </div>
                        <label className={`flex items-start gap-2 ${available ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                          <input
                            type="checkbox"
                            checked={isChecked && available}
                            disabled={!available}
                            onChange={(e) => { e.stopPropagation(); toggleClip(r.id, i, r.payload.clips); }}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1 accent-red-600 w-3.5 h-3.5 rounded border-white/10"
                          />
                          <div>
                            <p className="text-[9px] font-black uppercase text-slate-500 mb-0.5">
                              Clip {i + 1}{!available && <span className="text-amber-400 ml-1">(no video)</span>}
                            </p>
                            <p className="text-[10px] leading-relaxed text-slate-400 line-clamp-2 group-hover:text-slate-200">
                              {clip.description}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8 text-center">
                  <div className="bg-red-600/5 p-4 rounded border border-red-500/10">
                    <h4 className="text-[9px] font-black uppercase text-red-500 mb-2 tracking-widest">Instagram Caption</h4>
                    <p className="text-xs text-slate-300 leading-relaxed italic">"{r.payload.social.caption}"</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded border border-white/10">
                    <h4 className="text-[9px] font-black uppercase text-slate-500 mb-2 tracking-widest">Audio Script (15s)</h4>
                    <p className="text-xs text-slate-300 leading-relaxed">"{r.payload.social.audioScript}"</p>
                  </div>
                </div>

                {/* GENERATE VIDEO + PROGRESS */}
                <div className="mb-8">
                  <button
                    disabled={isRendering}
                    onClick={(e) => {
                      e.stopPropagation();
                      generateVideo(r.id, r.payload.clips);
                    }}
                    className="w-full bg-white text-black hover:bg-red-600 hover:text-white py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRendering ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Zap size={14} fill="currentColor" />
                    )}
                    {isRendering ? 'Generating...' : 'Generate Video'}
                  </button>

                  {/* PROGRESS BAR */}
                  {rs && rs.stage !== 'done' && (
                    <div className="space-y-2 mt-3">
                      <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            rs.stage === 'error' ? 'bg-red-500' : 'bg-red-600'
                          }`}
                          style={{
                            width: rs.stage === 'error'
                              ? '100%'
                              : `${Math.max(5, rs.progress)}%`,
                          }}
                        />
                      </div>
                      <p className="text-[10px] font-mono text-center tracking-wide">
                        {rs.stage === 'rendering' && (
                          <span className="text-sky-400">
                            Rendering video... {rs.progress}%
                          </span>
                        )}
                        {rs.stage === 'error' && (
                          <span className="text-red-400">
                            Error: {rs.error}
                          </span>
                        )}
                      </p>
                      {rs.stage !== 'error' && (
                        <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                          <span className="flex items-center gap-1">
                            <Timer size={10} />
                            {formatElapsed(elapsed[r.id] ?? 0)}
                          </span>
                          {rs.fileSizeBytes != null && rs.fileSizeBytes > 0 && (
                            <span className="flex items-center gap-1">
                              <HardDrive size={10} />
                              {formatFileSize(rs.fileSizeBytes)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* DONE STATE */}
                  {rs?.stage === 'done' && rs.videoUrl && (
                    <div className="space-y-2 mt-3">
                      <a
                        href={rs.videoUrl}
                        onClick={(e) => handleDownload(e, rs.videoUrl!, `${r.game_date}_${r.away_team}@${r.home_team}.mp4`, `render-${r.id}`)}
                        className={`w-full py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 ${downloading[`render-${r.id}`] ? 'bg-slate-700 text-slate-400 pointer-events-none' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
                        download
                      >
                        {downloading[`render-${r.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        {downloading[`render-${r.id}`] ? 'Downloading...' : 'Download Video'}
                      </a>
                      <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                        {rs.startedAt && rs.finishedAt && (
                          <span className="flex items-center gap-1">
                            <Timer size={10} className="text-emerald-500" />
                            <span className="text-emerald-500">{formatElapsed(Math.floor((rs.finishedAt - rs.startedAt) / 1000))}</span>
                          </span>
                        )}
                        {rs.fileSizeBytes != null && rs.fileSizeBytes > 0 && (
                          <span className="flex items-center gap-1">
                            <HardDrive size={10} className="text-emerald-500" />
                            <span className="text-emerald-500">{formatFileSize(rs.fileSizeBytes)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-4 items-end bg-black/40 p-4 rounded-lg border border-white/5">
                  <div className="flex-1">
                    <label className="text-[9px] font-black uppercase text-slate-500 mb-2 block tracking-widest">Modify Player or Prompt</label>
                    <textarea
                      placeholder="e.g. Please use Jalen Brunson as the Featured player instead"
                      className="w-full bg-slate-900/50 border border-white/10 rounded p-3 text-xs text-white placeholder:text-slate-600 focus:border-red-500 outline-none h-14 resize-none transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <button className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded text-[10px] font-black uppercase tracking-tighter transition-all">Re-Generate AI</button>
                    <button className="bg-white/5 hover:bg-white/10 text-slate-500 hover:text-white px-6 py-2 rounded text-[9px] font-bold uppercase transition-all">Reset to Default</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </>
  );
}
